declare global {
  interface Window {
    isKenttaNativeApp?: boolean
    ReactNativeWebView?: {
      postMessage(message: string): void
    }
  }
}

export type NfcTagData = {
  tagId: string
  busNumber?: string
  location?: string
  tagType?: string
  rawPayload: string
}

export type NfcAvailability = {
  supported: boolean | null
  enabled: boolean | null
}

export type NfcScanResult =
  | { type: 'nfc-scan-result'; data: NfcTagData }
  | { type: 'nfc-scan-error'; error: string }
  | { type: 'nfc-scan-cancelled' }
  | { type: 'nfc-disabled' }
  | { type: 'nfc-not-supported' }

export const NFC_SCAN_TIMEOUT_MS = 60000
const NFC_AVAILABILITY_TIMEOUT_MS = 5000

let nfcScanSequence = 0

type NfcScanBridgeResult = NfcScanResult & { scanId?: number }

function isNfcScanResult(detail: unknown): detail is NfcScanBridgeResult {
  if (!detail || typeof detail !== 'object' || !('type' in detail)) {
    return false
  }

  return [
    'nfc-scan-result',
    'nfc-scan-error',
    'nfc-scan-cancelled',
    'nfc-disabled',
    'nfc-not-supported',
  ].includes(String(detail.type))
}

export function isNativeApp(): boolean {
  return !!window.isKenttaNativeApp
}

export function requestNfcScan(signal?: AbortSignal): Promise<NfcScanResult> {
  if (!isNativeApp()) {
    return Promise.resolve({ type: 'nfc-not-supported' })
  }

  if (signal?.aborted) {
    return Promise.resolve({ type: 'nfc-scan-cancelled' })
  }

  return new Promise((resolve) => {
    let settled = false
    const scanId = ++nfcScanSequence

    const postMessage = (type: 'nfc-scan-request' | 'nfc-scan-cancel') => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type, scanId }))
    }

    const finish = (result: NfcScanResult) => {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(timeout)
      window.removeEventListener('kentta-native-message', handler)
      signal?.removeEventListener('abort', handleAbort)
      resolve(result)
    }

    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (!isNfcScanResult(detail)) {
        return
      }
      if (detail.scanId !== undefined && detail.scanId !== scanId) {
        return
      }
      finish(detail as NfcScanResult)
    }

    const handleAbort = () => {
      postMessage('nfc-scan-cancel')
      finish({ type: 'nfc-scan-cancelled' })
    }

    const timeout = window.setTimeout(() => {
      postMessage('nfc-scan-cancel')
      finish({ type: 'nfc-scan-error', error: 'timeout' })
    }, NFC_SCAN_TIMEOUT_MS)

    window.addEventListener('kentta-native-message', handler)
    signal?.addEventListener('abort', handleAbort)
    postMessage('nfc-scan-request')
  })
}

export function openNfcSettings(): void {
  if (!isNativeApp()) {
    return
  }

  window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'nfc-open-settings' }))
}

export function requestNfcAvailability(): Promise<NfcAvailability> {
  if (!isNativeApp()) {
    return Promise.resolve({ supported: false, enabled: false })
  }

  return new Promise((resolve) => {
    let settled = false
    const requestId = ++nfcScanSequence

    const finish = (availability: NfcAvailability) => {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(timeout)
      window.removeEventListener('kentta-native-message', handler)
      resolve(availability)
    }

    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.type !== 'nfc-availability' || detail.requestId !== requestId) {
        return
      }

      finish(detail.availability)
    }

    const timeout = window.setTimeout(() => {
      finish({ supported: null, enabled: null })
    }, NFC_AVAILABILITY_TIMEOUT_MS)

    window.addEventListener('kentta-native-message', handler)
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'nfc-availability-request', requestId })
    )
  })
}
